import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { UserLayout } from '../components/User/UserLayout';
import {
  CreditCard,
  Wallet,
  CheckCircle,
  Loader,
  Crown,
  Zap,
  Shield,
  Building2,
  Check,
  Phone,
  X,
  RefreshCw,
  Clock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  PLANS,
  ADDONS,
  USD_TO_THB_RATE,
  formatThb,
  formatUsd,
  usdToThb,
  isAddonId,
  type PlanId,
  type Tier,
  type AddonId,
  type Addon,
} from '../constants/pricing';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const BILLING_PROVIDER_MODE = import.meta.env.VITE_BILLING_PROVIDER_MODE || 'pending';

type ChannelKey = 'promptpay' | 'truemoney' | 'linepay' | 'scb' | 'kbank';

interface PaymentChannel {
  id: string;
  nameKey: ChannelKey;
  icon: string;
  available: boolean;
  requiresPhone?: boolean;
}

const paymentChannels: PaymentChannel[] = [
  { id: 'promptpay', nameKey: 'promptpay', icon: '🏦', available: true },
  { id: 'truemoney', nameKey: 'truemoney', icon: '💰', available: true, requiresPhone: true },
  { id: 'rabbit_linepay', nameKey: 'linepay', icon: '💚', available: true },
  { id: 'mobile_banking_scb', nameKey: 'scb', icon: '🟣', available: true },
  { id: 'mobile_banking_kbank', nameKey: 'kbank', icon: '🟢', available: true },
];

/** Bullet list shown in the order summary per tier.
 *
 *  Kept short on purpose — this is the post-decision confirmation step,
 *  not the comparison page. The full feature matrix lives in /upgrade. */
const TIER_FEATURES: Record<Tier, string[]> = {
  free: [],
  pro: ['pages', 'customDomains', 'pixelManager', 'affiliatePostback', 'deviceRedirect', 'geoFence'],
  agency: ['pages', 'customDomains', 'cloaking', 'aiSafePages', 'metaCapi', 'audienceExport', 'multiplePixels'],
  enterprise: ['pages', 'customDomains', 'cloaking', 'aiSafePages', 'gtmAutoSync', 'capiReplay', 'whiteLabel', 'prioritySupport'],
};

const TIER_ICON: Record<Tier, React.ReactNode> = {
  free:       <Crown className="w-8 h-8 text-white" />,
  pro:        <Zap className="w-8 h-8 text-white" />,
  agency:     <Shield className="w-8 h-8 text-white" />,
  enterprise: <Building2 className="w-8 h-8 text-white" />,
};

const TIER_HEADER_BG: Record<Tier, string> = {
  free:       'bg-gradient-to-br from-gray-500 to-gray-700',
  pro:        'bg-gradient-to-br from-blue-500 to-cyan-600',
  agency:     'bg-gradient-to-br from-purple-600 to-pink-600',
  enterprise: 'bg-gradient-to-br from-amber-500 to-orange-600',
};

/** Default to pro_monthly when callers land here without a plan param —
 *  same fallback behavior as the previous (single-plan) checkout. */
const DEFAULT_PLAN: PlanId = 'pro_monthly';

export const PaymentCheckout: React.FC = () => {
  const { t } = useTranslation('billing');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuthStore();

  // Phase 4 — checkout supports two product types via mutually-
  // exclusive query params:
  //   ?addon=<id>   → Phase 4 add-on slot purchase
  //   ?plan=<id>    → subscription upgrade (legacy default)
  // Addon takes precedence when both are present so an old tab can't
  // accidentally re-charge a subscription on top of an addon click.
  const rawAddon = searchParams.get('addon');
  const addonId: AddonId | null = rawAddon && isAddonId(rawAddon) ? rawAddon : null;
  const isAddonFlow = addonId !== null;
  const addon: Addon | null = addonId ? ADDONS[addonId] : null;

  const requestedPlan = searchParams.get('plan') || DEFAULT_PLAN;
  const planId: PlanId = (PLANS[requestedPlan as PlanId] ? requestedPlan : DEFAULT_PLAN) as PlanId;
  const plan = PLANS[planId];
  const tier: Tier = plan.tier;

  // Single source of truth for the THB amount we'll charge. Branches
  // on flow so the order summary + Pay button render consistently.
  const usdAmount = isAddonFlow && addon ? addon.usd : plan.usd;
  const thbAmount = usdToThb(usdAmount);

  const [selectedChannel, setSelectedChannel] = useState<string>('promptpay');
  const [isProcessing, setIsProcessing] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  // QR Code state (PromptPay)
  const [showQrCode, setShowQrCode] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [pollingOrderNo, setPollingOrderNo] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  const [pendingBillingIntent, setPendingBillingIntent] = useState<{
    id: string;
    invoiceId?: string;
    message?: string;
  } | null>(null);

  const selectedChannelInfo = paymentChannels.find((c) => c.id === selectedChannel);
  const isPendingProviderMode = BILLING_PROVIDER_MODE !== 'live';

  // If the URL plan was unknown, normalise it back to default in the
  // bar. Skip when we're in the addon flow — `requestedPlan` will
  // hold the default but we don't want to overwrite ?addon=… with
  // ?plan=…
  useEffect(() => {
    if (isAddonFlow) return;
    if (requestedPlan !== planId) {
      navigate(`/payment-checkout?plan=${planId}`, { replace: true });
    }
  }, [requestedPlan, planId, navigate, isAddonFlow]);

  // Poll for payment status (used for QR code / PromptPay payments)
  const pollPaymentStatus = useCallback(
    async (orderNo: string) => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/payments/check-status/${orderNo}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await response.json();

        if (response.ok && data.success) {
          if (data.payment.status === 'success' || data.payment.status === 'completed') {
            setPaymentStatus('success');
            await checkAuth();
            toast.success(t('checkout.toast.paymentSuccess'));
            setTimeout(() => {
              navigate(`/payment-success?order_no=${orderNo}`);
            }, 1500);
            return true;
          } else if (data.payment.status === 'failed') {
            setPaymentStatus('failed');
            toast.error(t('checkout.toast.paymentFailed'));
            return true;
          }
        }
        return false;
      } catch (error) {
        console.error('Poll error:', error);
        return false;
      }
    },
    [checkAuth, navigate, t]
  );

  useEffect(() => {
    if (!pollingOrderNo || paymentStatus !== 'pending') return;

    let timeoutId: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      const done = await pollPaymentStatus(pollingOrderNo);
      if (!done && !cancelled) {
        timeoutId = setTimeout(poll, 3000);
      }
    };

    timeoutId = setTimeout(poll, 2000);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [pollingOrderNo, paymentStatus, pollPaymentStatus]);

  const handlePayment = async () => {
    if (isPendingProviderMode) {
      setIsProcessing(true);
      try {
        const token = localStorage.getItem('token');
        const endpoint = isAddonFlow
          ? `${API_URL}/billing/intents/addon`
          : `${API_URL}/billing/intents/subscription`;
        const body = isAddonFlow
          ? { addonId }
          : { plan: planId, cycle: plan.cycle };

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok) {
          // Already have a pending invoice — send user to history instead of
          // creating a duplicate.
          if (response.status === 409 && data.code === 'OPEN_INVOICE_EXISTS') {
            toast.error(
              t('checkout.toast.openInvoiceExists', {
                orderNo: data.orderNo || '',
                defaultValue:
                  'คุณมีใบแจ้งหนี้ค้างชำระอยู่แล้ว ({{orderNo}}) กรุณาชำระหรือยกเลิกก่อนสร้างใหม่',
              }),
            );
            navigate('/payment-history');
            return;
          }
          throw new Error(data.error || t('checkout.toast.createFailed'));
        }

        setPendingBillingIntent({
          id: data.intent?.id,
          invoiceId: data.invoice?.id,
          message: data.message,
        });
        toast.success(t('checkout.pendingProvider.toast'));
      } catch (error: unknown) {
        console.error('Billing Intent Error:', error);
        const message = error instanceof Error ? error.message : t('checkout.toast.createFailed');
        toast.error(message);
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    if (!selectedChannel) {
      toast.error(t('checkout.toast.selectChannel'));
      return;
    }

    if (selectedChannel === 'truemoney' && !phoneNumber.trim()) {
      toast.error(t('checkout.toast.phoneRequired'));
      return;
    }

    setIsProcessing(true);

    try {
      const token = localStorage.getItem('token');
      const endpoint = isAddonFlow
        ? `${API_URL}/payments/create-addon-payment`
        : `${API_URL}/payments/create-subscription-payment`;
      const body = isAddonFlow
        ? {
            addon: addonId,
            channel: selectedChannel,
            phoneNumber: selectedChannel === 'truemoney' ? phoneNumber.trim() : undefined,
          }
        : {
            plan: planId,
            channel: selectedChannel,
            phoneNumber: selectedChannel === 'truemoney' ? phoneNumber.trim() : undefined,
          };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment');
      }

      const paymentData = data.payment;

      if (paymentData.paymentType === 'qr_code' && paymentData.qrCodeUrl) {
        setQrCodeUrl(paymentData.qrCodeUrl);
        setPollingOrderNo(paymentData.orderNo);
        setPaymentStatus('pending');
        setShowQrCode(true);
        setIsProcessing(false);
        toast.success(t('checkout.toast.qrReady'));
      } else if (paymentData.paymentUrl) {
        toast.dismiss();
        toast.success(t('checkout.toast.redirecting'));
        window.location.href = paymentData.paymentUrl;
      } else {
        throw new Error('No payment URL or QR code received');
      }
    } catch (error: unknown) {
      console.error('Payment Error:', error);
      toast.dismiss();
      const message = error instanceof Error ? error.message : t('checkout.toast.createFailed');
      toast.error(message);
      setIsProcessing(false);
    }
  };

  const handleCloseQr = () => {
    setShowQrCode(false);
    setQrCodeUrl(null);
    setPollingOrderNo(null);
    setPaymentStatus('pending');
  };

  // QR Code Modal (for PromptPay)
  if (showQrCode && qrCodeUrl) {
    return (
      <UserLayout>
        <div className="max-w-lg mx-auto p-8">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-8 text-center">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">{t('checkout.qr.title')}</h2>
              <button
                onClick={handleCloseQr}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {paymentStatus === 'pending' && (
              <div className="flex items-center justify-center gap-2 mb-4 text-blue-600">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-sm font-medium">{t('checkout.qr.waiting')}</span>
              </div>
            )}
            {paymentStatus === 'success' && (
              <div className="flex items-center justify-center gap-2 mb-4 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-bold">{t('checkout.qr.success')}</span>
              </div>
            )}
            {paymentStatus === 'failed' && (
              <div className="flex items-center justify-center gap-2 mb-4 text-red-600">
                <X className="w-5 h-5" />
                <span className="text-sm font-bold">{t('checkout.qr.failed')}</span>
              </div>
            )}

            <div className="bg-white border-2 border-gray-100 rounded-xl p-4 inline-block mb-6">
              <img src={qrCodeUrl} alt={t('checkout.qr.imageAlt')} className="w-64 h-64 mx-auto" />
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">{t('checkout.qr.amountLabel')}</p>
              <p className="text-3xl font-bold text-blue-600">{formatThb(thbAmount)}</p>
            </div>

            <div className="text-left bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm font-semibold text-gray-900 mb-2">{t('checkout.qr.instructionsTitle')}</p>
              <ol className="text-sm text-gray-700 space-y-1.5 list-decimal list-inside">
                <li>{t('checkout.qr.step1')}</li>
                <li>{t('checkout.qr.step2')}</li>
                <li>{t('checkout.qr.step3')}</li>
                <li>{t('checkout.qr.step4')}</li>
              </ol>
            </div>

            {paymentStatus === 'failed' && (
              <button
                onClick={handleCloseQr}
                className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-all"
              >
                {t('checkout.qr.retry')}
              </button>
            )}

            <p className="text-xs text-gray-500 mt-4">{t('checkout.qr.expiry')}</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  if (pendingBillingIntent) {
    return (
      <UserLayout>
        <div className="max-w-2xl mx-auto p-8">
          <div className="bg-white rounded-2xl border border-amber-200 shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('checkout.paymentPending.title')}
            </h1>
            <p className="text-gray-600 mb-6">
              {pendingBillingIntent.message || t('checkout.paymentPending.body')}
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left mb-6">
              <p className="text-sm font-semibold text-amber-900 mb-2">
                {t('checkout.pendingProvider.title')}
              </p>
              <p className="text-sm text-amber-800">
                {t('checkout.pendingProvider.body')}
              </p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-amber-900">
                <div>
                  <span className="font-semibold">{t('checkout.paymentPending.intentId')}</span>
                  <p className="font-mono break-all">{pendingBillingIntent.id}</p>
                </div>
                {pendingBillingIntent.invoiceId && (
                  <div>
                    <span className="font-semibold">{t('checkout.paymentPending.invoiceId')}</span>
                    <p className="font-mono break-all">{pendingBillingIntent.invoiceId}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/payment-history')}
                className="flex-1 py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-all"
              >
                {t('checkout.paymentPending.viewHistory')}
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 py-3 px-6 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-semibold transition-all"
              >
                {t('checkout.paymentPending.backDashboard')}
              </button>
            </div>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="max-w-5xl mx-auto p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-16 h-16 ${TIER_HEADER_BG[tier]} rounded-full mb-4`}>
            {TIER_ICON[tier]}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isAddonFlow && addon
              ? addon.name
              : t('checkout.headerTitle', { plan: t(`checkout.plan.${planId}.name`) })}
          </h1>
          <p className="text-gray-600">{t('checkout.headerSubtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Payment Methods */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                {isPendingProviderMode ? t('checkout.pendingProvider.methodTitle') : t('checkout.selectMethod')}
              </h2>

              {isPendingProviderMode ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
                  <div className="flex gap-3">
                    <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-900">{t('checkout.pendingProvider.title')}</p>
                      <p className="text-sm text-amber-800 mt-1">{t('checkout.pendingProvider.body')}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {paymentChannels.map((channel) => (
                    <button
                      key={channel.id}
                      onClick={() => setSelectedChannel(channel.id)}
                      disabled={!channel.available || isProcessing}
                      className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                        selectedChannel === channel.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      } ${!channel.available ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{channel.icon}</span>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {t(`checkout.channel.${channel.nameKey}`)}
                            </p>
                            <p className="text-sm text-gray-600">
                              {t(`checkout.channel.${channel.nameKey}Desc`)}
                            </p>
                          </div>
                        </div>
                        {selectedChannel === channel.id && <CheckCircle className="w-6 h-6 text-blue-600" />}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!isPendingProviderMode && selectedChannelInfo?.requiresPhone && (
                <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <label className="flex items-center gap-2 text-sm font-medium text-orange-900 mb-2">
                    <Phone className="w-4 h-4" />
                    {t('checkout.phoneLabel')}
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="0812345678"
                    maxLength={10}
                    className="w-full px-4 py-2.5 border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white text-gray-900"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-orange-700 mt-1.5">{t('checkout.phoneHint')}</p>
                </div>
              )}

              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">
                    {isPendingProviderMode ? t('checkout.manualActivation.title') : t('checkout.secureTitle')}
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    {isPendingProviderMode ? t('checkout.manualActivation.body') : t('checkout.secureDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('checkout.summary.heading')}</h3>

              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">{t('checkout.summary.plan')}</span>
                  <span className="font-semibold text-gray-900">
                    {isAddonFlow && addon ? addon.name : t(`checkout.plan.${planId}.name`)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">{t('checkout.summary.duration')}</span>
                  <span className="font-semibold text-gray-900">
                    {isAddonFlow && addon
                      ? t(`addons.cycle.${addon.cycle}`)
                      : t(`checkout.plan.${planId}.duration`)}
                  </span>
                </div>
              </div>

              <div className="border-t border-gray-300 pt-4 mb-4">
                <div className="flex justify-between items-baseline">
                  <span className="text-lg font-semibold text-gray-900">{t('checkout.summary.total')}</span>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">{formatUsd(usdAmount)}</div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {t('checkout.summary.totalThb', { thb: thbAmount.toLocaleString('th-TH') })}
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 mt-2 leading-snug">
                  {t('checkout.summary.fxNote', { rate: USD_TO_THB_RATE })}
                </p>
                {isAddonFlow && addon?.cycle === 'one_shot' && (
                  <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                    {t('addons.aiPackHint')}
                  </p>
                )}
              </div>

              {!isAddonFlow && TIER_FEATURES[tier].length > 0 && (
                <div className="space-y-2 mb-6">
                  <p className="text-sm font-semibold text-gray-900 mb-2">{t('checkout.summary.includes')}</p>
                  {TIER_FEATURES[tier].map((featureKey) => (
                    <div key={featureKey} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">
                        {t(`checkout.plan.feature.${featureKey}`)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={handlePayment}
                disabled={isProcessing}
                className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2 ${
                  isProcessing
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                }`}
              >
                {isProcessing ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    {t('checkout.actions.processing')}
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    {isPendingProviderMode
                      ? t('checkout.actions.requestManualActivation')
                      : t('checkout.actions.pay', { amount: thbAmount.toLocaleString('th-TH') })}
                  </>
                )}
              </button>

              <button
                onClick={() => navigate('/upgrade')}
                disabled={isProcessing}
                className="w-full mt-3 py-2 px-6 rounded-lg font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 transition-all"
              >
                {t('checkout.actions.changePlan')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </UserLayout>
  );
};
